import {
  PhlebotomyFinderAction,
  PhlebotomyLocation,
  MobilePhlebotomyLink,
  MobilePhlebotomyPhoneNumber,
} from "../pages/Phlebotomy/types";
import { BASE_URL } from "../utils/requestUtils";
import {
  createPhlebotomySearchError,
  PhlebotomySearchErrorCode,
  parsePhlebotomySearchErrorCode,
} from "./PhlebotomySearchError";
import { MobilePhlebotomyRequestConflictError } from "./MobilePhlebotomyRequestError";

export { MobilePhlebotomyRequestConflictError } from "./MobilePhlebotomyRequestError";

// Backend API response types
interface BackendPhlebotomyLocation {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  latitude?: string;
  longitude?: string;
  phone_number?: string;
  phone_extension?: string;
  metadata: string[];
  scheduling_url?: string;
  full_address: string;
  full_phone?: string;
  coordinates: [number, number];
  created_at: string;
  updated_at: string;
}

interface BackendMobilePhlebotomyUrl {
  id: number;
  name: string;
  url: string;
  metadata: string[];
  created_at: string;
  updated_at: string;
}

interface BackendMobilePhlebotomyPhone {
  id: number;
  name: string;
  number: string;
  extension?: string;
  is_call_to_book: boolean;
  is_text_to_book: boolean;
  metadata: string[];
  full_phone: string;
  created_at: string;
  updated_at: string;
}

interface BackendLocationWithOrder {
  id: number;
  display_order: number;
  phlebotomy_location: BackendPhlebotomyLocation;
  created_at: string;
  updated_at: string;
}

interface BackendUrlWithOrder {
  id: number;
  display_order: number;
  mobile_phlebotomy_url: BackendMobilePhlebotomyUrl;
  created_at: string;
  updated_at: string;
}

interface BackendPhoneWithOrder {
  id: number;
  display_order: number;
  mobile_phlebotomy_phone: BackendMobilePhlebotomyPhone;
  created_at: string;
  updated_at: string;
}

interface BackendPhlebotomyServicesResponse {
  portal_account: {
    public_id: string;
  };
  locations: {
    count: number;
    results: BackendLocationWithOrder[];
  };
  mobile_services: {
    count: number;
    results: BackendUrlWithOrder[];
  };
  mobile_phones: {
    count: number;
    results: BackendPhoneWithOrder[];
  };
}

// Frontend response type
export interface PhlebotomyServicesData {
  portalAccount: {
    publicId: string;
  };
  locations: PhlebotomyLocation[];
  mobilePhlebotomyLinks: MobilePhlebotomyLink[];
  mobilePhlebotomyPhoneNumbers: MobilePhlebotomyPhoneNumber[];
}

// Global location search types
interface BackendSearchLocation extends BackendPhlebotomyLocation {
  distance_miles: number | null;
}

interface BackendLocationSearchResponse {
  search: {
    latitude: number;
    longitude: number;
  };
  search_radius_miles?: number;
  portal_account?: {
    public_id: string;
    account_name: string | null;
  };
  locations: {
    count: number;
    results: BackendSearchLocation[];
  };
}

interface BackendFinderResult {
  display_name: string;
  site_type: "brick_and_mortar" | "bto_psc" | "mobile_phlebotomy";
  distance_miles: number | null;
  address: {
    line_1: string;
    city: string;
    state: string;
    postal_code: string;
  } | null;
  coordinates: { latitude: number; longitude: number } | null;
  hours: string;
  appointments?: string;
  is_preferred: boolean;
  notes?: string;
  actions: Array<{
    action_type: PhlebotomyFinderAction["type"];
    target: string;
    is_primary: boolean;
  }>;
}

interface BackendFinderSearchResponse {
  search: BackendLocationSearchResponse["search"];
  search_radius_miles?: number;
  results: {
    count: number;
    results: BackendFinderResult[];
  };
}

const splitCatalogMetadataLines = (value: string, delimiter: string) =>
  value
    .split(delimiter)
    .map((line) => line.trim())
    .filter(Boolean);

export type PhlebotomySearchOrigin =
  | { lat: number; lng: number; zip?: string }
  | { zip: string };

export interface PhlebotomySearchOptions {
  portalAccountPublicId?: string;
  useSalesforceCatalog?: boolean;
  finderSessionId?: string;
}

export interface PhlebotomySearchPortalAccount {
  publicId: string;
  accountName: string | null;
}

interface PhlebotomySearchData {
  search: {
    latitude: number;
    longitude: number;
  };
  searchRadiusMiles: number | null;
  locations: PhlebotomyLocation[];
  portalAccount?: PhlebotomySearchPortalAccount;
}

interface ReminderSubmissionData {
  first_name: string;
  last_name: string;
  phone_number: string;
  estimated_due_date: string;
  submitted_by: "patient" | "provider";
  portal_account_public_id?: string;
  tracking_id?: string;
}

// Reminder opt-in submission types
export interface PhlebotomyReminderSubmissionData {
  first_name: string;
  last_name: string;
  phone_number: string;
  estimated_due_date?: string | null;
  postal_code: string;
  submitted_by: "patient" | "provider";
  portal_account_public_id?: string;
  tracking_id?: string;
}

export interface MobilePhlebotomyRequestData {
  request_id?: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  date_of_birth: string;
  estimated_due_date: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  preferred_date: string;
  preferred_time_window: "morning" | "afternoon" | "anytime";
  alternate_date?: string;
  alternate_time_window?: "morning" | "afternoon" | "anytime";
  flexible_scheduling: boolean;
  has_kit?: boolean;
  scheduling_notes?: string;
  portal_account_public_id?: string;
  tracking_id?: string;
  language: "en" | "es";
  contact_consent: boolean;
}

export interface MobilePhlebotomyRequestResponse {
  requestId: string;
}

export class PhlebotomyApiService {
  private static readonly API_PATH = "/api/v1/phlebotomy_service";
  private static readonly REMINDER_API_PATH = "/api/v1/phlebotomy_reminder";
  private static readonly LOCATION_SEARCH_API_PATH =
    "/api/v1/phlebotomy_location_search";
  private static readonly FINDER_SEARCH_API_PATH =
    "/api/v1/phlebotomy_finder_search";
  private static readonly MOBILE_REQUEST_API_PATH =
    "/api/v1/mobile_phlebotomy_request";

  /**
   * Converts E164 phone number to frontend format (10-digit US number)
   * E164 format: +14155551234 -> 4155551234
   * Already digits-only: 4155551234 -> 4155551234
   */
  private static formatPhoneNumber(phoneNumber: string): string {
    // Remove all non-digits first
    const digitsOnly = phoneNumber.replace(/\D/g, "");

    // If it starts with country code 1 and has 11 digits (US E164), remove the 1
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      return digitsOnly.substring(1);
    }

    // If it's already 10 digits, return as-is
    if (digitsOnly.length === 10) {
      return digitsOnly;
    }

    // For other cases, return the digits as-is (could be international)
    return digitsOnly;
  }

  /**
   * Transforms backend phlebotomy location to frontend format
   */
  private static transformLocation(
    backendLocation: BackendPhlebotomyLocation,
  ): PhlebotomyLocation {
    const location: PhlebotomyLocation = {
      name: backendLocation.name,
      address: backendLocation.address,
      city: backendLocation.city,
      state: backendLocation.state,
      zipCode: backendLocation.postal_code,
      coordinates: {
        lat: backendLocation.coordinates[0],
        lng: backendLocation.coordinates[1],
      },
    };

    if (backendLocation.metadata?.length) {
      location.metadata = backendLocation.metadata;
    }

    // Add phone if available
    if (backendLocation.phone_number) {
      location.phone = {
        number: this.formatPhoneNumber(backendLocation.phone_number),
        extension: backendLocation.phone_extension || undefined,
      };
    }

    // Add scheduling URL if available
    if (backendLocation.scheduling_url) {
      location.schedulingUrl = backendLocation.scheduling_url;
    }

    return location;
  }

  /**
   * Transforms backend mobile phlebotomy URL to frontend format
   */
  private static transformMobileUrl(
    backendUrl: BackendMobilePhlebotomyUrl,
  ): MobilePhlebotomyLink {
    return {
      name: backendUrl.name,
      link: backendUrl.url,
      notes: backendUrl.metadata,
    };
  }

  /**
   * Transforms backend mobile phlebotomy phone to frontend format
   */
  private static transformMobilePhone(
    backendPhone: BackendMobilePhlebotomyPhone,
  ): MobilePhlebotomyPhoneNumber {
    return {
      name: backendPhone.name,
      number: this.formatPhoneNumber(backendPhone.number),
      extension: backendPhone.extension || undefined,
      callToBook: backendPhone.is_call_to_book,
      textToBook: backendPhone.is_text_to_book,
      notes: backendPhone.metadata,
    };
  }

  /**
   * Transforms the full backend response to frontend format
   */
  private static transformResponse(
    backendResponse: BackendPhlebotomyServicesResponse,
  ): PhlebotomyServicesData {
    return {
      portalAccount: {
        publicId: backendResponse.portal_account.public_id,
      },
      locations: backendResponse.locations.results.map((item) =>
        this.transformLocation(item.phlebotomy_location),
      ),
      mobilePhlebotomyLinks: backendResponse.mobile_services.results.map(
        (item) => this.transformMobileUrl(item.mobile_phlebotomy_url),
      ),
      mobilePhlebotomyPhoneNumbers: backendResponse.mobile_phones.results.map(
        (item) => this.transformMobilePhone(item.mobile_phlebotomy_phone),
      ),
    };
  }

  /**
   * Fetches phlebotomy services using a Portal Account public ID.
   */
  static async getPhlebotomyServices(
    portalAccountPublicId: string,
  ): Promise<PhlebotomyServicesData> {
    const queryParam = `?portal_account_public_id=${encodeURIComponent(portalAccountPublicId)}`;
    const endpoint = `${BASE_URL}${this.API_PATH}/${queryParam}`;

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Account not found: ${portalAccountPublicId}`);
        }
        throw new Error(
          `Failed to fetch phlebotomy services: ${response.status} ${response.statusText}`,
        );
      }

      const backendData: BackendPhlebotomyServicesResponse =
        await response.json();
      return this.transformResponse(backendData);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        `Network error while fetching phlebotomy services: ${error}`,
      );
    }
  }

  /**
   * Searches for phlebotomy locations near a ZIP code or latitude/longitude
   * origin. The server always searches within a fixed radius.
   */
  static async searchPhlebotomyLocations(
    origin: PhlebotomySearchOrigin,
    options?: PhlebotomySearchOptions,
  ): Promise<PhlebotomySearchData> {
    const useSalesforceCatalog = Boolean(
      options?.useSalesforceCatalog &&
      !options.portalAccountPublicId &&
      options.finderSessionId,
    );
    const hasCoordinateOrigin = "lat" in origin;
    const params = new URLSearchParams();
    if (!hasCoordinateOrigin) {
      params.set("zip", origin.zip);
    } else {
      params.set("lat", String(origin.lat));
      params.set("lng", String(origin.lng));
      if (useSalesforceCatalog && origin.zip) {
        params.set("zip", origin.zip);
      }
    }
    if (options?.portalAccountPublicId) {
      params.set("portal_account_public_id", options.portalAccountPublicId);
    }
    if (useSalesforceCatalog) {
      params.set("finder_session_id", options?.finderSessionId ?? "");
    }
    let endpoint = `${BASE_URL}${
      useSalesforceCatalog
        ? this.FINDER_SEARCH_API_PATH
        : this.LOCATION_SEARCH_API_PATH
    }/?${params.toString()}`;

    let response: Response;
    try {
      response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });
      // Client and server flags can briefly disagree while LaunchDarkly updates.
      // A dark server endpoint fails closed; retain the existing universal reader.
      if (useSalesforceCatalog && response.status === 404) {
        params.delete("finder_session_id");
        if (hasCoordinateOrigin) {
          params.delete("zip");
        }
        endpoint = `${BASE_URL}${this.LOCATION_SEARCH_API_PATH}/?${params.toString()}`;
        response = await fetch(endpoint, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
      }
    } catch (error) {
      throw createPhlebotomySearchError(
        `Network error while searching phlebotomy locations: ${error}`,
        "unknown",
      );
    }

    if (!response.ok) {
      let code: PhlebotomySearchErrorCode = "unknown";
      let message = `Failed to search phlebotomy locations: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        code = parsePhlebotomySearchErrorCode(errorData.code);
        if (errorData.error) {
          message = errorData.error;
        }
      } catch {
        // Use default message and code
      }
      throw createPhlebotomySearchError(message, code);
    }

    if (
      useSalesforceCatalog &&
      endpoint.includes(this.FINDER_SEARCH_API_PATH)
    ) {
      const backendData: BackendFinderSearchResponse = await response.json();
      return {
        search: backendData.search,
        searchRadiusMiles: backendData.search_radius_miles ?? null,
        locations: backendData.results.results.map((result) => {
          const appointments = result.appointments?.trim();
          const hours = splitCatalogMetadataLines(result.hours, ";");
          const siteNotes = result.notes
            ? splitCatalogMetadataLines(result.notes, "|")
            : [];
          const phoneAction = result.actions.find(
            (action) =>
              action.action_type === "call" || action.action_type === "text",
          );
          return {
            name: result.display_name,
            address: result.address?.line_1 ?? "",
            city: result.address?.city ?? "",
            state: result.address?.state ?? "",
            zipCode: result.address?.postal_code ?? "",
            coordinates: result.coordinates
              ? {
                  lat: result.coordinates.latitude,
                  lng: result.coordinates.longitude,
                }
              : null,
            ...(phoneAction
              ? {
                  phone: {
                    number: this.formatPhoneNumber(phoneAction.target),
                  },
                }
              : {}),
            metadata: [...hours, ...(appointments ? [appointments] : [])],
            ...(siteNotes.length ? { siteNotes } : {}),
            distanceMiles: result.distance_miles ?? undefined,
            siteType: result.site_type,
            isPreferred: result.is_preferred,
            finderActions: result.actions.map((action) => ({
              type: action.action_type,
              target: action.target,
              isPrimary: action.is_primary,
            })),
          };
        }),
      };
    }

    const backendData: BackendLocationSearchResponse = await response.json();
    return {
      search: {
        latitude: backendData.search.latitude,
        longitude: backendData.search.longitude,
      },
      searchRadiusMiles: backendData.search_radius_miles ?? null,
      locations: backendData.locations.results.map((backendLocation) => ({
        ...this.transformLocation(backendLocation),
        distanceMiles: backendLocation.distance_miles ?? undefined,
      })),
      portalAccount: backendData.portal_account
        ? {
            publicId: backendData.portal_account.public_id,
            accountName: backendData.portal_account.account_name,
          }
        : undefined,
    };
  }

  /**
   * Legacy reminder submission retained until the retired reminder page is removed.
   */
  static async submitReminder(data: ReminderSubmissionData): Promise<void> {
    const endpoint = `${BASE_URL}${this.REMINDER_API_PATH}/`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        let errorMessage = `Failed to submit reminder: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.phone_number) {
            errorMessage = "Please enter a valid US phone number";
          } else if (typeof errorData === "object") {
            const firstError = Object.values(errorData)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMessage = String(firstError[0]);
            }
          }
        } catch {
          // Use default error message.
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Network error while submitting reminder: ${error}`);
    }
  }

  /**
   * Creates an explicit phlebotomy text-reminder opt-in.
   */
  static async submitPhlebotomyReminder(
    data: PhlebotomyReminderSubmissionData,
  ): Promise<void> {
    const endpoint = `${BASE_URL}${this.REMINDER_API_PATH}/`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        // Try to get error message from response body
        let errorMessage = `Failed to submit reminder: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.phone_number) {
            errorMessage = "Please enter a valid US phone number";
          } else if (typeof errorData === "object") {
            const firstError = Object.values(errorData)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMessage = String(firstError[0]);
            }
          }
        } catch {
          // Use default error message
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Network error while submitting reminder: ${error}`);
    }
  }

  /** Sends a mobile-draw help request to the Customer Support queue. */
  static async submitMobilePhlebotomyRequest(
    data: MobilePhlebotomyRequestData,
  ): Promise<MobilePhlebotomyRequestResponse> {
    const endpoint = `${BASE_URL}${this.MOBILE_REQUEST_API_PATH}/`;
    let response: Response;
    try {
      response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw new Error(`Network error while submitting request: ${error}`);
    }

    if (!response.ok) {
      if (response.status === 409) {
        try {
          const conflict: { request_id?: string } = await response.json();
          if (conflict.request_id) {
            throw new MobilePhlebotomyRequestConflictError(conflict.request_id);
          }
        } catch (error) {
          if (error instanceof MobilePhlebotomyRequestConflictError) {
            throw error;
          }
        }
      }
      throw new Error(
        `Failed to submit mobile phlebotomy request: ${response.status} ${response.statusText}`,
      );
    }

    const responseData: { request_id: string } = await response.json();
    return { requestId: responseData.request_id };
  }
}
